import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { Checklist, ChecklistItem, Organization } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Button, Card, Input, Spinner } from '@/components/ui';

function ChecklistItemRow({
  item,
  onToggle,
  onDelete,
  canDelete,
}: {
  item: ChecklistItem;
  onToggle: (item: ChecklistItem) => void;
  onDelete?: (itemId: string) => void;
  canDelete?: boolean;
}) {
  const isReady = item.status === 'ready';

  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-white/60 px-3 py-2.5 transition-colors hover:bg-accent/5">
      <input
        type="checkbox"
        checked={isReady}
        onChange={() => onToggle(item)}
        className="h-4 w-4 shrink-0 rounded border-border accent-primary"
      />
      <span className={`flex-1 text-sm ${isReady ? 'text-muted-foreground line-through' : ''}`}>
        {item.title}
      </span>
      <Badge variant={isReady ? 'success' : 'destructive'}>{isReady ? 'Hazır' : 'Eksik'}</Badge>
      {canDelete && onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onDelete(item.id);
          }}
          className="text-muted-foreground hover:text-destructive"
          aria-label="Sil"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </label>
  );
}

function ChecklistSection({
  title,
  subtitle,
  items,
  onToggle,
  onDelete,
  canDeleteGeneral,
}: {
  title: string;
  subtitle?: string;
  items: ChecklistItem[];
  onToggle: (item: ChecklistItem) => void;
  onDelete?: (itemId: string) => void;
  canDeleteGeneral?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-semibold text-primary">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {items.map((item) => (
        <ChecklistItemRow
          key={item.id}
          item={item}
          onToggle={onToggle}
          onDelete={onDelete}
          canDelete={item.isPersonal || canDeleteGeneral}
        />
      ))}
    </div>
  );
}

export function ChecklistsPage() {
  const { user, effectiveIsSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState('');
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [addingGeneralTo, setAddingGeneralTo] = useState<string | null>(null);
  const [personalTitle, setPersonalTitle] = useState('');
  const [generalTitle, setGeneralTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [addingGeneral, setAddingGeneral] = useState(false);

  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
  });

  const orgId = orgs?.organizations[0]?.id;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['checklists', orgId],
    queryFn: () => api<{ checklists: Checklist[] }>(`/checklists/org/${orgId}`),
    enabled: !!orgId,
  });

  /* Bir maddeye dokununca işaret hemen değişiyor, sunucu beklenmiyor.
     Eskiden önce istek gidiyor, sonra bütün liste yeniden çekiliyordu:
     iki gidiş-dönüş boyunca kutucuk olduğu gibi duruyor, dokunuş
     işlememiş gibi görünüyordu. İstek arkada sürüyor; hata olursa
     işaret eski hâline dönüyor ve liste tazeleniyor. */
  async function toggleItem(item: ChecklistItem) {
    const yeniDurum = item.status === 'ready' ? 'missing' : 'ready';
    const eskiDurum = item.status;

    /* Madde üç dizide birden geçebiliyor (items, generalItems,
       personalItems); ekran hangisini çiziyorsa o değişsin diye
       üçünde de güncelleniyor. */
    const yaz = (durum: string | undefined) => {
      const cevir = (liste?: ChecklistItem[]) =>
        liste?.map((m) => (m.id === item.id ? { ...m, status: durum } : m));
      queryClient.setQueryData<{ checklists: Checklist[] }>(['checklists', orgId], (onceki) =>
        onceki
          ? {
              checklists: onceki.checklists.map((l) => ({
                ...l,
                items: cevir(l.items) ?? l.items,
                generalItems: cevir(l.generalItems),
                personalItems: cevir(l.personalItems),
              })),
            }
          : onceki,
      );
    };

    yaz(yeniDurum);
    try {
      await api(`/checklists/items/${item.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: yeniDurum }),
      });
    } catch {
      yaz(eskiDurum);
      await refetch();
    }
  }

  async function deleteItem(itemId: string) {
    if (!confirm('Bu maddeyi silmek istediğinize emin misiniz?')) return;
    await api(`/checklists/items/${itemId}`, { method: 'DELETE' });
    await refetch();
  }

  async function seedDefault() {
    if (!orgId) return;
    setSeeding(true);
    setError('');
    try {
      await api(`/checklists/org/${orgId}/seed-default`, { method: 'POST' });
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Oluşturulamadı');
    } finally {
      setSeeding(false);
    }
  }

  async function addGeneralItem(checklistId: string) {
    if (!orgId || !generalTitle.trim()) return;
    setAddingGeneral(true);
    setError('');
    try {
      await api(`/checklists/org/${orgId}/general-items`, {
        method: 'POST',
        body: JSON.stringify({ checklistId, title: generalTitle.trim() }),
      });
      setGeneralTitle('');
      setAddingGeneralTo(null);
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Eklenemedi');
    } finally {
      setAddingGeneral(false);
    }
  }

  async function addPersonalItem(checklistId: string) {
    if (!orgId || !personalTitle.trim()) return;
    setAdding(true);
    setError('');
    try {
      await api(`/checklists/org/${orgId}/personal-items`, {
        method: 'POST',
        body: JSON.stringify({ checklistId, title: personalTitle.trim() }),
      });
      setPersonalTitle('');
      setAddingTo(null);
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Eklenemedi');
    } finally {
      setAdding(false);
    }
  }

  const checklists = data?.checklists ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Hazırlık Listesi"
        subtitle="Kampa gitmeden önce eşyalarınızı kontrol edin"
      />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="h-8 w-8" />
        </div>
      ) : checklists.length === 0 ? (
        <Card className="text-center">
          <p className="text-muted-foreground">Henüz checklist yok</p>
          {(effectiveIsSuperAdmin || orgId) && (
            <Button className="mt-4" variant="gold" onClick={seedDefault} disabled={seeding}>
              {seeding ? <Spinner /> : 'Varsayılan Listeyi Ekle'}
            </Button>
          )}
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </Card>
      ) : (
        checklists.map((checklist) => {
          const generalItems = checklist.generalItems ?? checklist.items.filter((i) => !i.isPersonal);
          const personalItems = checklist.personalItems ?? checklist.items.filter((i) => i.isPersonal);
          const allItems = [...generalItems, ...personalItems];
          const readyCount = allItems.filter((i) => i.status === 'ready').length;
          const total = allItems.length;
          const percentage = total > 0 ? Math.round((readyCount / total) * 100) : 0;

          return (
            <Card key={checklist.id}>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="font-serif text-lg font-semibold text-primary">{checklist.title}</h3>
                  {checklist.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{checklist.description}</p>
                  )}
                </div>
                <Badge variant={percentage === 100 ? 'success' : 'gold'}>%{percentage}</Badge>
              </div>

              <div className="space-y-5">
                <ChecklistSection
                  title="Genel Liste"
                  subtitle="Tüm katılımcılar için ortak maddeler"
                  items={generalItems}
                  onToggle={toggleItem}
                  onDelete={deleteItem}
                  canDeleteGeneral={effectiveIsSuperAdmin}
                />

                {effectiveIsSuperAdmin && addingGeneralTo === checklist.id ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Genel madde ekle..."
                      value={generalTitle}
                      onChange={(e) => setGeneralTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addGeneralItem(checklist.id);
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="gold"
                      disabled={addingGeneral || !generalTitle.trim()}
                      onClick={() => addGeneralItem(checklist.id)}
                    >
                      {addingGeneral ? <Spinner /> : 'Ekle'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddingGeneralTo(null)}>
                      İptal
                    </Button>
                  </div>
                ) : effectiveIsSuperAdmin ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setAddingGeneralTo(checklist.id);
                      setGeneralTitle('');
                      setAddingTo(null);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Genel Madde Ekle
                  </Button>
                ) : null}

                <ChecklistSection
                  title="Kişisel Liste"
                  subtitle={`${user?.fullName ?? 'Sizin'} eklediğiniz maddeler`}
                  items={personalItems}
                  onToggle={toggleItem}
                  onDelete={deleteItem}
                />

                {addingTo === checklist.id ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Kişisel madde ekle..."
                      value={personalTitle}
                      onChange={(e) => setPersonalTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addPersonalItem(checklist.id);
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="gold"
                      disabled={adding || !personalTitle.trim()}
                      onClick={() => addPersonalItem(checklist.id)}
                    >
                      {adding ? <Spinner /> : 'Ekle'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddingTo(null)}>
                      İptal
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setAddingTo(checklist.id);
                      setPersonalTitle('');
                      setAddingGeneralTo(null);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Kişisel Madde Ekle
                  </Button>
                )}
              </div>
            </Card>
          );
        })
      )}

      {error && checklists.length > 0 && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
